import React from 'react';
import { colorFromName, initialFromName } from '../utils/colorHash';

/**
 * ClinicCard
 * Displays a single clinic's information in a card layout.
 *
 * @param {{ clinic: object }} props
 */
export default function ClinicCard({ clinic }) {
  const {
    name,
    address,
    phone,
    line_id,
    google_maps_url,
    region,
    province,
    logo_url,
    products = [],
  } = clinic;

  // Filter to products the clinic actually stocks
  const stockedProducts = products.filter((p) => p.stock_status && p.stock_status !== 'none');

  return (
    <div className="cs-card">
      {/* ── Header ──────────────────────────────────────────────── */}
      {/* Header is now a 2-column flex: logo/avatar on the left, then
          name + location/distance badges on the right. Falls back to a
          deterministic colored circle (hashed from the clinic name) when
          no logo_url is provided by the backend. */}
      <div className="cs-card__header">
        <ClinicAvatar logoUrl={logo_url} name={name} />
        <div className="cs-card__header-text">
          <h3 className="cs-card__name">{name}</h3>
          <div className="cs-card__location-badges">
            {province && (
              <span className="cs-badge cs-badge--province">{province}</span>
            )}
            {region && (
              <span className="cs-badge cs-badge--region">{region}</span>
            )}
            {typeof clinic.distance === 'number' && (
              <span className="cs-badge cs-badge--distance">
                {clinic.distance < 1
                  ? `${Math.round(clinic.distance * 1000)} ม.`
                  : `${clinic.distance.toFixed(1)} กม.`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Address ─────────────────────────────────────────────── */}
      {address && (
        <div className="cs-card__address">
          <span className="cs-card__icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              <circle cx="12" cy="9" r="2.5"/>
            </svg>
          </span>
          <span className="cs-card__address-text">{address}</span>
        </div>
      )}

      {/* ── Products ────────────────────────────────────────────── */}
      {/* v1.0.2 UI tweak: products are now horizontal colored chips
          (deterministic color via colorFromName — same product always gets
          the same chip color across cards / reloads). Replaces the old
          vertical list of ProductBadge rows. */}
      {stockedProducts.length > 0 && (
        <div className="cs-card__products">
          <p className="cs-card__products-label">สินค้า</p>
          <div className="cs-card__products-chips">
            {stockedProducts.map((product) => (
              <span
                key={product.product_id}
                className="cs-product-chip"
                style={{ backgroundColor: colorFromName(product.name) }}
              >
                {product.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Contact actions ──────────────────────────────────────── */}
      <div className="cs-card__actions">
        {phone && (
          <a
            href={`tel:${phone.replace(/\s|-/g, '')}`}
            className="cs-action-btn cs-action-btn--phone"
            title={`โทร ${phone}`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.12 1.2 2 2 0 012.11 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{phone}</span>
          </a>
        )}

        {line_id && (
          <a
            href={`https://line.me/ti/p/${encodeURIComponent(line_id)}`}
            className="cs-action-btn cs-action-btn--line"
            target="_blank"
            rel="noopener noreferrer"
            title={`LINE: ${line_id}`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
            </svg>
            <span>{line_id}</span>
          </a>
        )}

        {google_maps_url && (
          <a
            href={google_maps_url}
            className="cs-action-btn cs-action-btn--maps"
            target="_blank"
            rel="noopener noreferrer"
            title="ดูแผนที่"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6-10l6-3m0 13l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Google Maps</span>
          </a>
        )}
      </div>
    </div>
  );
}

// ── Clinic avatar ────────────────────────────────────────────────────────────
// Renders the uploaded logo when available, otherwise a colored circle with
// the first letter of the clinic name. Color is hashed from the name so the
// same clinic always gets the same color (deterministic across reloads /
// pagination / filter changes).
function ClinicAvatar({ logoUrl, name }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name ? `${name} logo` : 'Clinic logo'}
        className="cs-clinic-logo"
        loading="lazy"
      />
    );
  }
  return (
    <div
      className="cs-avatar-fallback"
      style={{ backgroundColor: colorFromName(name) }}
      aria-hidden="true"
    >
      {initialFromName(name)}
    </div>
  );
}
